import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { useCart } from "@/contexts/CartContext";
import { CheckCircle, Loader2, AlertCircle, Package } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const isPreorder = searchParams.get("preorder") === "true";
  const [status, setStatus] = useState("checking"); // checking, paid, failed
  const [orderId, setOrderId] = useState("");
  const { clearCart } = useCart();
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    const pollStatus = async () => {
      try {
        if (isPreorder) {
          const { data } = await axios.get(`${API}/api/preorder/check-payment/${sessionId}`);
          if (data.status === "confirmed") {
            setStatus("paid");
            setOrderId(data.preorder_id || "");
            return;
          }
          // Pending means Stripe hasn't confirmed yet - keep polling
        } else {
          const { data } = await axios.get(`${API}/api/checkout/status/${sessionId}`);
          if (data.payment_status === "paid") {
            setStatus("paid");
            setOrderId(data.order_id || "");
            clearCart();
            return;
          }
          if (data.status === "expired") {
            setStatus("failed");
            return;
          }
        }
        if (attempts < 20) {
          setTimeout(() => setAttempts(a => a + 1), 3000);
        } else {
          setStatus("failed");
        }
      } catch {
        if (attempts < 20) {
          setTimeout(() => setAttempts(a => a + 1), 3000);
        } else {
          setStatus("failed");
        }
      }
    };

    pollStatus();
  }, [sessionId, attempts, clearCart, isPreorder]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#050505" }}>
      <div className="max-w-md w-full mx-4">
        <div className="border border-[#1A1A1A] bg-[#0D0D0D] p-12 text-center">
          {status === "checking" && (
            <>
              <Loader2 className="h-12 w-12 text-[#39FF14] mx-auto mb-6 animate-spin" strokeWidth={1.5} />
              <h1 className="text-2xl font-bold uppercase tracking-tight text-[#F2F2F2] mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
                Processing Payment
              </h1>
              <p className="text-[#A1A1A1] text-sm">Verifying your payment with Stripe...</p>
            </>
          )}

          {status === "paid" && (
            <>
              <CheckCircle className="h-12 w-12 text-[#39FF14] mx-auto mb-6" strokeWidth={1.5} />
              <h1 data-testid="payment-success" className="text-2xl font-bold uppercase tracking-tight text-[#F2F2F2] mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
                {isPreorder ? "Preorder Confirmed" : "Payment Successful"}
              </h1>
              {orderId && (
                <div className="border border-[#39FF14]/30 bg-[#39FF14]/5 p-4 mb-4">
                  <p className="font-mono text-[10px] text-[#39FF14] uppercase tracking-[0.2em] mb-1">Confirmation Number</p>
                  <p className="font-mono text-lg text-[#F2F2F2] font-bold" data-testid="confirmation-number">#{orderId.slice(-8)}</p>
                </div>
              )}
              <p className="text-[#A1A1A1] text-sm mb-6">
                {isPreorder
                  ? "Your preorder deposit has been received. Your product is guaranteed! We'll notify you when it's ready to ship."
                  : "Thank you for your purchase. A confirmation email has been sent. We'll email you again with tracking info when your order ships."}
              </p>
              <div className="flex flex-col gap-3">
                <Link to="/orders">
                  <Button className="w-full bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold py-6">
                    <Package className="h-4 w-4 mr-2" />
                    {isPreorder ? "View My Preorders" : "View Orders"}
                  </Button>
                </Link>
                <Link to="/products">
                  <Button variant="outline" className="w-full bg-transparent border-[#1A1A1A] hover:border-[#39FF14] text-[#A1A1A1] hover:text-[#39FF14] rounded-none font-mono text-xs uppercase py-6">
                    Continue Shopping
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === "failed" && (
            <>
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-6" strokeWidth={1.5} />
              <h1 data-testid="payment-failed" className="text-2xl font-bold uppercase tracking-tight text-[#F2F2F2] mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
                Payment Issue
              </h1>
              <p className="text-[#A1A1A1] text-sm mb-8">There was a problem processing your payment. Please try again.</p>
              <Link to="/cart">
                <Button className="w-full bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold py-6">
                  Return to Cart
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
